import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { RegionIcon } from '@design/components/region-icon/RegionIcon';
import { ColorScheme } from '@design/design-tokens/themes/theme-names';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { NonLinearStepper } from '@terminal/components/non-linear-stepper/NonLinearStepper';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { useTranslation } from 'react-i18next';

import styles from './GeographicStepperSection.module.scss';

interface GeographicStepperSectionProps {
  entity: Entity;
  targetName: string;
  targetType: 'country' | 'region';
  title?: string;
  description?: string;
  activeIndex: number;
}

export const GeographicStepperSection = ({
  entity,
  targetName,
  targetType,
  title,
  activeIndex,
  description,
}: GeographicStepperSectionProps) => {
  const { t } = useTranslation(['widget']);
  const isDark = useThemeStore(state => state.colorScheme === ColorScheme.Dark);
  const logoUrl = isDark
    ? entity.logo_urls.light_png
    : entity.logo_urls.dark_png;

  return (
    <div className={styles.sectionRow} data-testid="geographic-stepper-section">
      {title && <div className={styles.titleMedium}>{title}</div>}
      <div className={styles.stepperWrapper}>
        <NonLinearStepper
          steps={[
            {
              logoUrl: logoUrl,
              hoverLabel: formatTicker(entity),
              connectorLabel: t(
                'widget:assetOverview.knowledgeGraph.exposedTo'
              ),
              isSimple: false,
            },
            {
              imgNode:
                targetType === 'country' ? (
                  <FlagIcon
                    countryCode={targetName}
                    variant="circular"
                    customSize={23}
                  />
                ) : (
                  <RegionIcon region={targetName} size={23} />
                ),
              hoverLabel: targetName,
              iconColor: 'var(--kg-theme)',
            },
          ]}
          activeIndex={activeIndex}
          iconSize={23}
        />
      </div>
      {description && <div className={styles.bodySmall}>{description}</div>}
    </div>
  );
};
